import {writable} from 'svelte/store';
import Helper from '../Models/Helper';
import Pattern from '../Models/Pattern';

const content = {version: {}, cache: {}, queue: [], requesting: {}, batch_count: 0, batch_timeout: 0, batch_loaded: false};
const data = writable(content);

function setBatchBusy() {
	content.batch_count++;
	clearTimeout(content.batch_timeout);
	content.batch_loaded = false;
	data.set(content);
}

function setBatchLoad() {
	content.batch_count--;
	if (content.batch_count === 0) {
		const proceed = () => {
			content.batch_loaded = true;
			data.set(content);
		};

		content.batch_timeout = setTimeout(proceed, 250);
		data.set(content);
	}

	data.set(content);
}

function setCache(uri, response) {
	content.cache[uri] = response;
	if (content.requesting[uri]) {
		for (const item of content.requesting[uri])
			item.resolve(response);

		delete content.requesting[uri];
	}

	data.set(content);
}

/*
 * The mirror of setCache for a request that cannot be fulfilled. Without it the
 * batch counter never comes back down and the reader sits on the loader for
 * good, because setBatchLoad() is only reached on the success path.
 */
function setFailed(uri, error) {
	console.error(`Data request failed: ${uri}`, error);
	setBatchLoad();
	if (content.requesting[uri]) {
		for (const item of content.requesting[uri])
			item.reject(error);

		delete content.requesting[uri];
	}

	data.set(content);
}

function requestJSON(uri) {
	setBatchBusy();
	const url = `./data/json/${uri}.json`;
	fetch(url).then(response => {
		/*
		 * fetch does not reject on 404, and .htaccess used to answer a missing
		 * data file with site_*.html and a 200 — so check the status rather than
		 * letting response.json() fail on an error page.
		 */
		if (!response.ok) throw new Error(`${response.status} ${response.statusText} for ${url}`);
		return response.json();
	}).then(source => {
		setBatchLoad();
		setCache(uri, source);
	}).catch(error => setFailed(uri, error));
}

function requestJSONP(resolve, reject, uri) {
	const next = request => {
		JSONP.busy = true;
		const url = `./data/jsonp/${request.uri}.js`;
		const script = document.createElement('script');
		script.type = 'text/javascript';
		script.crossOrigin = 'anonymous';
		script.src = url;

		JSONP.onSet(source => {
			jQuery(script).remove();
			setBatchLoad();
			JSONP.busy = false;
			/*
			 * request.uri, not the uri this closure was created with: next() drains
			 * the queue from inside the first request's closure, so using that one
			 * files every following payload under the first request's key.
			 */
			setCache(request.uri, source);
			request.resolve(source);
			if (content.queue.length) {
				const item = content.queue.shift();
				data.set(content);
				next(item);
			}
		});

		jQuery('body').append(script);
	};

	setBatchBusy();
	content.queue.push({uri, resolve, reject});
	data.set(content);

	if (!JSONP.busy) {
		const item = content.queue.shift();
		data.set(content);
		next(item);
	}
}

/* Public */

data.init = async () => {
	await data.version();
};

data.version = async () => {
	let item = {};
	const versions = await data.absolute('versions');

	if (Helper.app()) {
		const html = window.document.querySelector('html');
		const code = html.getAttribute('data-version') || 'BB';
		for (const version of versions)
			if (version.code === code) {
				item = version;
				break;
			}
	}

	else {
		const host = window.location.hostname;
		for (const version of versions)
			if (version.hosts.includes(host)) {
				item = version;
				break;
			}

		/*
		 * On localhost the locally running API wins, so a dev frontend never talks
		 * to production. Copied rather than mutated, because item points into the
		 * cached versions array.
		 */
		if (['localhost', '127.0.0.1'].includes(host) && Helper.is(item.api_dev))
			item = Object.assign({}, item, {api: item.api_dev});
	}

	content.version = item;
	data.set(content);
};

data.absolute = uri => {
	return new Promise((resolve, reject) => {
		if (Helper.undefined(content.cache[uri])) {
			if (!Helper.app()) {
				const requesting = !!content.requesting[uri];
				if (!requesting) content.requesting[uri] = [];
				content.requesting[uri].push({resolve, reject});
				if (!requesting) requestJSON(uri);
			}
			else requestJSONP(resolve, reject, uri);
		}
		else resolve(content.cache[uri]);
	});
};

data.relative = uri => {
	const url = `${content.version.code}/${uri}`;
	return data.absolute(url);
};

export default data;